import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, Edit, Mail, Phone, MapPin, Building2, Briefcase, 
  Calendar, User, FileText, MessageSquare, Activity,
  ChevronDown, ChevronUp, Plus, Target, Users, Package, 
  CheckSquare, Globe, Newspaper, FolderOpen, Trash2, UserCheck,
  Save, X, DollarSign, ClipboardList, Send, Lock, Unlock, Receipt,
  AlertTriangle, Eye, EyeOff
} from 'lucide-react';
import { QuoteCalculatorForm } from '../components/QuoteCalculatorForm';
import { LeadActivityPanel } from '../components/LeadActivityPanel';
import { EmailComposerModal } from '../components/EmailComposerModal';

const LEAD_STATUSES = [
  { id: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-700' },
  { id: 'proposal', label: 'Proposal', color: 'bg-orange-100 text-orange-700' },
  { id: 'won', label: 'Won', color: 'bg-green-100 text-green-700' },
  { id: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700' },
];

const PRIORITIES = [
  { id: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600' },
  { id: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'high', label: 'High', color: 'bg-red-100 text-red-700' },
];

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg).filter(Boolean).join(', ') || fallback;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  return fallback;
};

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-3 px-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <Icon className="w-4 h-4 text-[#014DB7]" />
          {title}
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

const EditableAccordion = ({ title, icon: Icon, children, editContent, onSave, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave();
      setIsEditing(false);
      toast.success('Saved successfully');
    } catch (error) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-3 px-4 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-[#014DB7]">
          <Icon className="w-4 h-4" />
          {title}
        </div>
        <div className="flex items-center gap-2">
          {isOpen && !isEditing && (
            <span 
              onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
              className="text-xs text-gray-500 hover:text-[#014DB7] cursor-pointer flex items-center gap-1"
            >
              <Edit className="w-3 h-3" /> Edit
            </span>
          )}
          {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>
      {isOpen && (
        <div className="p-4 bg-white">
          {isEditing ? (
            <div>
              {editContent}
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setIsEditing(false)}
                  className="rounded-lg"
                >
                  <X className="w-3 h-3 mr-1" /> Cancel
                </Button>
                <Button 
                  size="sm" 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#014DB7] rounded-lg"
                >
                  <Save className="w-3 h-3 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
};

const InfoItem = ({ label, value, icon: Icon }) => (
  <div className="flex items-start gap-2 py-1">
    {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />}
    <div className="min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm text-[#0A1628] truncate">{value || '-'}</p>
    </div>
  </div>
);

const TabButton = ({ active, icon: Icon, label, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
      active ? 'border-[#014DB7] text-[#014DB7]' : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

const TagList = ({ items, color = 'blue' }) => {
  if (!items?.length) return <span className="text-sm text-gray-400">None</span>;
  const colorClasses = color === 'green' ? 'bg-[#84CC16]/10 text-[#65a30d]' : 'bg-[#014DB7]/10 text-[#014DB7]';
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, idx) => (
        <span key={idx} className={`text-xs px-2 py-1 rounded-full ${colorClasses}`}>{item}</span>
      ))}
    </div>
  );
};

export const LeadDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [lead, setLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({});
  const [quotes, setQuotes] = useState([]);
  const [editFields, setEditFields] = useState({});
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockPin, setUnlockPin] = useState('');
  const [showLeadEmailModal, setShowLeadEmailModal] = useState(false);
  const [quoteToUnlock, setQuoteToUnlock] = useState(null);

  // Email logs & stats state
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [loadingEmails, setLoadingEmails] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadRes, usersRes, quotesRes] = await Promise.all([
          api.get(`/api/leads/${id}`),
          api.get('/api/users'),
          api.get(`/api/leads/${id}/quotes`).catch(() => ({ data: { quotes: [] } })),
        ]);
        const leadData = leadRes.data;
        setLead(leadData);
        setEditFields({ ...leadData });
        setFormData({
          ...leadData,
          status: leadData.status || 'new',
          priority: leadData.priority || 'medium',
        });
        setUsers(usersRes.data);
        setQuotes(quotesRes.data.quotes || []);
      } catch (error) {
        toast.error('Failed to load lead');
        navigate('/crm/leads');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (activeTab === 'emails' && id) {
      fetchEmailLogs();
    }
  }, [activeTab, id]);

  const fetchEmailLogs = async () => {
    setLoadingEmails(true);
    try {
      const leadEmail = lead?.email;
      const params = new URLSearchParams();
      params.append('lead_id', id);
      if (leadEmail) params.append('email', leadEmail);
      const [logsRes, statsRes] = await Promise.all([
        api.get(`/api/email/logs?${params.toString()}`),
        api.get(`/api/email/stats?${params.toString()}`),
      ]);
      setEmailLogs(logsRes.data.logs || []);
      setEmailStats(statsRes.data);
    } catch (err) {
      // silent fail
    } finally {
      setLoadingEmails(false);
    }
  };



  const handleFieldSave = async (fields) => {
    try {
      const updateData = {};
      fields.forEach(f => { updateData[f] = editFields[f]; });
      await api.put(`/api/leads/${id}`, updateData);
      setLead(prev => ({ ...prev, ...updateData }));
    } catch (error) {
      throw error;
    }
  };

  const calculateQuoteTotal = (items) => {
    return (items || []).reduce((sum, item) => sum + (item.quantity || 0) * (item.unit_price || 0), 0);
  };

  const handleAddQuote = async () => {
    if (!newQuote.name.trim()) {
      toast.error('Quote name is required');
      return;
    }
    if (!newQuote.items || newQuote.items.length === 0 || !newQuote.items[0].description) {
      toast.error('Add at least one line item');
      return;
    }
    try {
      const quoteData = {
        ...newQuote,
        total: calculateQuoteTotal(newQuote.items)
      };
      const res = await api.post(`/api/leads/${id}/quotes`, quoteData);
      setQuotes([...quotes, res.data.quote]);
      setNewQuote({ name: '', items: [{ description: '', quantity: 1, unit_price: 0 }], notes: '', valid_until: '' });
      setShowAddQuote(false);
      toast.success('Quote created');
    } catch (error) {
      toast.error('Failed to create quote');
    }
  };

  const handleUpdateQuote = async (quoteId, updates) => {
    try {
      const res = await api.put(`/api/leads/${id}/quotes/${quoteId}`, {
        ...updates,
        total: calculateQuoteTotal(updates.items)
      });
      setQuotes(quotes.map(q => q.id === quoteId ? { ...q, ...updates, total: calculateQuoteTotal(updates.items) } : q));
      setEditingQuote(null);
      toast.success('Quote updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update quote'));
    }
  };

  const handleUnlockQuote = async () => {
    if (!unlockPin) {
      toast.error('Enter your PIN');
      return;
    }
    try {
      await api.post(`/api/leads/${id}/quotes/${quoteToUnlock}/unlock`, { pin: unlockPin });
      setQuotes(quotes.map(q => q.id === quoteToUnlock ? { ...q, is_locked: false, unlocked_at: new Date().toISOString() } : q));
      setShowUnlockModal(false);
      setUnlockPin('');
      setQuoteToUnlock(null);
      toast.success('Quote unlocked for editing');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Invalid PIN'));
    }
  };

  const handleConvertToInvoice = async (quote) => {
    if (!window.confirm('Convert this quote to an invoice? This will create an invoice for the client.')) return;
    try {
      const res = await api.post(`/api/leads/${id}/quotes/${quote.id}/convert-to-invoice`);
      toast.success(res.data.message || 'Quote converted to invoice');
      navigate('/crm/invoices');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to convert to invoice'));
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (quote?.is_locked) {
      toast.error('Cannot delete a locked quote. Unlock it first.');
      return;
    }
    if (!window.confirm('Delete this quote?')) return;
    try {
      await api.delete(`/api/leads/${id}/quotes/${quoteId}`);
      setQuotes(quotes.filter(q => q.id !== quoteId));
      toast.success('Quote deleted');
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const handleEmailQuote = async (quote) => {
    if (!lead.email) {
      toast.error('Lead has no email address');
      return;
    }
    try {
      await api.post(`/api/leads/${id}/quotes/${quote.id}/send-email`, {
        to_email: lead.email,
        lead_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.company_name || 'Valued Customer'
      });
      // Update quote in state to show locked status
      setQuotes(quotes.map(q => q.id === quote.id ? { ...q, is_locked: true, status: 'sent', sent_at: new Date().toISOString() } : q));
      toast.success(`Quote emailed to ${lead.email}. Quote is now locked.`);
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const handleSave = async () => {
    try {
      await api.put(`/api/leads/${id}`, formData);
      setLead(formData);
      setEditing(false);
      toast.success('Lead updated');
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleConvertToClient = async () => {
    if (!window.confirm('Convert this lead to a client? Their quotes will stay active and follow through.')) return;
    try {
      const response = await api.post(`/api/leads/${id}/convert-to-client`);
      toast.success(response.data.message || 'Lead converted to client!');
      navigate(`/crm/clients/${response.data.client_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead to client');
    }
  };

  const handleDeleteLead = async () => {
    if (!window.confirm('Delete this lead and move to "Already Pitched" archive?\n\nThis lead will be excluded from future NPI imports.')) {
      return;
    }
    
    try {
      await api.delete(`/api/leads/${id}/archive`);
      toast.success('Lead moved to Already Pitched archive');
      navigate('/crm/leads');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleAssignmentChange = async (userId) => {
    try {
      const assignedTo = userId === 'unassigned' ? null : userId;
      await api.put(`/api/leads/${id}`, { assigned_to: assignedTo });
      setLead(prev => ({ ...prev, assigned_to: assignedTo }));
      const assignee = users.find(u => u.id === userId);
      toast.success(assignee ? `Lead assigned to ${assignee.first_name} ${assignee.last_name}` : 'Lead unassigned');
    } catch (error) {
      toast.error('Failed to update assignment');
    }
  };

  const getStatusInfo = (status) => LEAD_STATUSES.find(s => s.id === status) || LEAD_STATUSES[0];
  const getPriorityInfo = (priority) => PRIORITIES.find(p => p.id === priority) || PRIORITIES[1];
  const getAssignedUser = () => users.find(u => u.id === lead?.assigned_to);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!lead) return null;

  const statusInfo = getStatusInfo(lead.status);
  const priorityInfo = getPriorityInfo(lead.priority);
  const assignedUser = getAssignedUser();

  return (
    <div className="flex gap-6 -m-6">
      {/* Left Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 min-h-[calc(100vh-64px)] flex flex-col">
        {/* Back Button */}
        <div className="p-4 border-b border-gray-100">
          <Link to="/crm/leads" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#014DB7]">
            <ArrowLeft className="w-4 h-4" />
            Back to Leads
          </Link>
        </div>

        {/* Lead Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div className="w-14 h-14 bg-gradient-to-br from-[#014DB7] to-[#0066FF] rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">
                {lead.first_name?.[0]}{lead.last_name?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-[#0A1628] text-lg">
                {lead.first_name} {lead.last_name}
              </h2>
              {lead.job_title && <p className="text-sm text-gray-500">{lead.job_title}</p>}
              {lead.company && <p className="text-sm text-[#014DB7] font-medium">{lead.company}</p>}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <span className={`text-xs px-3 py-1.5 rounded-lg font-medium ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
            <span className={`text-xs px-3 py-1.5 rounded-lg font-medium ${priorityInfo.color}`}>
              {priorityInfo.label} Priority
            </span>
          </div>

          <Button onClick={() => setEditing(true)} variant="outline" className="w-full mt-4 rounded-xl">
            <Edit className="w-4 h-4 mr-2" /> Edit Lead
          </Button>

          {lead.email && (
            <Button onClick={() => setShowLeadEmailModal(true)} variant="outline" className="w-full mt-2 text-blue-600 border-blue-200 hover:bg-blue-50 rounded-xl" data-testid="lead-email-btn">
              <Mail className="w-4 h-4 mr-2" /> Email Lead
            </Button>
          )}
          
          <Button 
            onClick={handleConvertToClient} 
            className="w-full mt-2 bg-[#014DB7] hover:bg-[#013a8c] text-white rounded-xl"
            data-testid="convert-to-client-btn"
          >
            <UserCheck className="w-4 h-4 mr-2" /> Convert to Client
          </Button>
          
          <Button 
            onClick={handleDeleteLead} 
            variant="outline"
            className="w-full mt-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-xl"
            data-testid="delete-lead-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete Lead
          </Button>
        </div>

        {/* Scrollable Sections */}
        <div className="flex-1 overflow-y-auto">
          {/* Contact Info */}
          <CollapsibleSection title="Contact" icon={Phone}>
            <div className="space-y-2">
              <InfoItem icon={Mail} label="Email" value={lead.email} />
              <InfoItem icon={Phone} label="Phone" value={lead.phone} />
              <InfoItem icon={Building2} label="Company" value={lead.company} />
            </div>
            
            {/* Delete Lead Button */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Button 
                onClick={handleDeleteLead}
                variant="outline"
                className="w-full text-red-500 border-red-200 hover:bg-red-50 hover:border-red-300 rounded-xl"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete Lead
              </Button>
              <p className="text-xs text-gray-400 mt-2 text-center">Moves to "Already Pitched" archive</p>
            </div>
          </CollapsibleSection>

          {/* Company Details */}
          <CollapsibleSection title="Company Details" icon={Building2}>
            <div className="space-y-2">
              <InfoItem icon={Briefcase} label="Job Title" value={lead.job_title} />
              <InfoItem icon={Users} label="Company Size" value={lead.company_size ? `${lead.company_size} employees` : null} />
              <InfoItem icon={Target} label="Current Solution" value={lead.current_solution} />
            </div>
          </CollapsibleSection>

          {/* Assignment */}
          <CollapsibleSection title="Assignment" icon={User}>
            {assignedUser ? (
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-[#84CC16] rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">{assignedUser.first_name?.[0]}{assignedUser.last_name?.[0]}</span>
                </div>
                <div>
                  <p className="text-sm font-medium">{assignedUser.first_name} {assignedUser.last_name}</p>
                  <p className="text-xs text-gray-400">{assignedUser.email}</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 mb-3">Unassigned</p>
            )}
            
            {/* Assignment dropdown for admin/super_admin */}
            {(user?.role === 'super_admin' || user?.role === 'admin') && (
              <div className="pt-2 border-t border-gray-100">
                <Label className="text-xs text-gray-500 mb-2 block">Assign to Salesperson</Label>
                <Select 
                  value={lead.assigned_to || 'unassigned'} 
                  onValueChange={handleAssignmentChange}
                >
                  <SelectTrigger className="rounded-xl h-9 text-sm" data-testid="lead-assign-select">
                    <SelectValue placeholder="Select salesperson" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.filter(u => u.role === 'sales' || u.role === 'admin' || u.role === 'super_admin').map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CollapsibleSection>

          {/* Source & Tracking */}
          <CollapsibleSection title="Source" icon={Globe}>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-gray-400 mb-1">Lead Source</p>
                <span className="text-xs bg-[#014DB7]/10 text-[#014DB7] px-2 py-1 rounded-lg font-medium">
                  {lead.source?.replace('_', ' ')}
                </span>
              </div>
              {lead.subscribed_newsletter && (
                <div className="flex items-center gap-2 text-sm text-[#84CC16]">
                  <Newspaper className="w-4 h-4" />
                  Subscribed to Newsletter
                </div>
              )}
            </div>
          </CollapsibleSection>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 py-6 pr-6">
        {/* Tabs */}
        <div className="bg-white rounded-t-2xl border border-gray-200 border-b-0">
          <div className="flex items-center gap-1 px-4 border-b border-gray-200">
            <TabButton active={activeTab === 'overview'} icon={User} label="Overview" onClick={() => setActiveTab('overview')} />
            <TabButton active={activeTab === 'quotes'} icon={ClipboardList} label={`Quotes (${quotes.length})`} onClick={() => setActiveTab('quotes')} />
            <TabButton active={activeTab === 'emails'} icon={Mail} label={`Emails${emailStats?.total > 0 ? ` (${emailStats.total})` : ''}`} onClick={() => setActiveTab('emails')} testId="lead-emails-tab" />
            <TabButton active={activeTab === 'activity'} icon={Activity} label="Activity" onClick={() => setActiveTab('activity')} />
            <TabButton active={activeTab === 'notes'} icon={FileText} label="Notes" onClick={() => setActiveTab('notes')} />
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-b-2xl border border-gray-200 border-t-0 p-6">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {/* Personal Information - Editable */}
              <EditableAccordion 
                title="PERSONAL INFORMATION" 
                icon={User} 
                defaultOpen={true}
                onSave={() => handleFieldSave(['first_name', 'last_name', 'job_title'])}
                editContent={
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">First Name</Label>
                      <Input 
                        value={editFields.first_name || ''} 
                        onChange={(e) => setEditFields({...editFields, first_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Last Name</Label>
                      <Input 
                        value={editFields.last_name || ''} 
                        onChange={(e) => setEditFields({...editFields, last_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Job Title</Label>
                      <Input 
                        value={editFields.job_title || ''} 
                        onChange={(e) => setEditFields({...editFields, job_title: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                }
              >
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">First Name</p>
                    <p className="font-medium text-[#0A1628]">{lead.first_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Last Name</p>
                    <p className="font-medium text-[#0A1628]">{lead.last_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Job Title</p>
                    <p className="font-medium text-[#0A1628]">{lead.job_title || '-'}</p>
                  </div>
                </div>
              </EditableAccordion>

              {/* Contact Information - Editable */}
              <EditableAccordion 
                title="CONTACT INFORMATION" 
                icon={Mail} 
                onSave={() => handleFieldSave(['email', 'phone', 'company'])}
                editContent={
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Email</Label>
                      <Input 
                        type="email"
                        value={editFields.email || ''} 
                        onChange={(e) => setEditFields({...editFields, email: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Phone</Label>
                      <Input 
                        value={editFields.phone || ''} 
                        onChange={(e) => setEditFields({...editFields, phone: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Company</Label>
                      <Input 
                        value={editFields.company || ''} 
                        onChange={(e) => setEditFields({...editFields, company: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                }
              >
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Email</p>
                    <p className="font-medium text-[#0A1628]">{lead.email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Phone</p>
                    <p className="font-medium text-[#0A1628]">{lead.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Company</p>
                    <p className="font-medium text-[#0A1628]">{lead.company || '-'}</p>
                  </div>
                </div>
              </EditableAccordion>

              {/* Company Information - Editable */}
              <EditableAccordion 
                title="COMPANY INFORMATION" 
                icon={Building2} 
                onSave={() => handleFieldSave(['company_size', 'current_solution'])}
                editContent={
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Company Size</Label>
                      <Input 
                        type="number"
                        value={editFields.company_size || ''} 
                        onChange={(e) => setEditFields({...editFields, company_size: e.target.value})}
                        placeholder="Number of employees"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Current Solution</Label>
                      <Select 
                        value={editFields.current_solution || 'none'} 
                        onValueChange={(v) => setEditFields({...editFields, current_solution: v})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="spreadsheets">Spreadsheets</SelectItem>
                          <SelectItem value="other-software">Other Software</SelectItem>
                          <SelectItem value="manual">Manual Process</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                }
              >
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Company Size</p>
                    <p className="font-medium text-[#0A1628]">{lead.company_size ? `${lead.company_size} employees` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Current Solution</p>
                    <p className="font-medium text-[#0A1628] capitalize">{lead.current_solution?.replace('-', ' ') || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Newsletter</p>
                    <p className="font-medium text-[#0A1628]">{lead.subscribed_newsletter ? 'Subscribed' : 'Not subscribed'}</p>
                  </div>
                </div>
              </EditableAccordion>

              {/* Interests - Editable */}
              <EditableAccordion 
                title="INTERESTS" 
                icon={CheckSquare} 
                onSave={() => handleFieldSave(['interests'])}
                editContent={
                  <div>
                    <Label className="text-xs text-gray-500 mb-2 block">Select interests (comma separated)</Label>
                    <Input 
                      value={(editFields.interests || []).join(', ')} 
                      onChange={(e) => setEditFields({...editFields, interests: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                      placeholder="Inventory Management, Billing, Patient Portal..."
                    />
                    <div className="mt-2 flex flex-wrap gap-1">
                      {['Inventory Management', 'Billing', 'Patient Portal', 'Scheduling', 'Reporting', 'E-Signature', 'Mobile App'].map(interest => (
                        <button
                          key={interest}
                          onClick={() => {
                            const current = editFields.interests || [];
                            if (current.includes(interest)) {
                              setEditFields({...editFields, interests: current.filter(i => i !== interest)});
                            } else {
                              setEditFields({...editFields, interests: [...current, interest]});
                            }
                          }}
                          className={`text-xs px-2 py-1 rounded-full transition-colors ${
                            (editFields.interests || []).includes(interest) 
                              ? 'bg-[#014DB7] text-white' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {interest}
                        </button>
                      ))}
                    </div>
                  </div>
                }
              >
                <TagList items={lead.interests} color="blue" />
              </EditableAccordion>

              {/* Licensed States - Editable */}
              <EditableAccordion 
                title="LICENSED STATES" 
                icon={MapPin} 
                onSave={() => handleFieldSave(['licensed_states'])}
                editContent={
                  <div>
                    <Label className="text-xs text-gray-500 mb-2 block">Select states (comma separated)</Label>
                    <Input 
                      value={(editFields.licensed_states || []).join(', ')} 
                      onChange={(e) => setEditFields({...editFields, licensed_states: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                      placeholder="FL, GA, AL, TX..."
                    />
                    <div className="mt-2 flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                      {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(state => (
                        <button
                          key={state}
                          onClick={() => {
                            const current = editFields.licensed_states || [];
                            if (current.includes(state)) {
                              setEditFields({...editFields, licensed_states: current.filter(s => s !== state)});
                            } else {
                              setEditFields({...editFields, licensed_states: [...current, state]});
                            }
                          }}
                          className={`text-xs px-2 py-1 rounded-full transition-colors ${
                            (editFields.licensed_states || []).includes(state) 
                              ? 'bg-[#014DB7] text-white' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {state}
                        </button>
                      ))}
                    </div>
                  </div>
                }
              >
                <TagList items={lead.licensed_states} color="blue" />
              </EditableAccordion>

              {/* DME Categories - Editable */}
              <EditableAccordion 
                title="DME CATEGORIES" 
                icon={Package} 
                onSave={() => handleFieldSave(['dme_categories'])}
                editContent={
                  <div>
                    <Label className="text-xs text-gray-500 mb-2 block">Select categories</Label>
                    <div className="flex flex-wrap gap-1">
                      {['Wheelchairs', 'Hospital Beds', 'Oxygen Equipment', 'CPAP/BiPAP', 'Walkers', 'Bathroom Safety', 'Diabetic Supplies', 'Wound Care', 'Orthopedic', 'Respiratory'].map(cat => (
                        <button
                          key={cat}
                          onClick={() => {
                            const current = editFields.dme_categories || [];
                            if (current.includes(cat)) {
                              setEditFields({...editFields, dme_categories: current.filter(c => c !== cat)});
                            } else {
                              setEditFields({...editFields, dme_categories: [...current, cat]});
                            }
                          }}
                          className={`text-xs px-2 py-1 rounded-full transition-colors ${
                            (editFields.dme_categories || []).includes(cat) 
                              ? 'bg-[#84CC16] text-white' 
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </div>
                }
              >
                <TagList items={lead.dme_categories} color="green" />
              </EditableAccordion>

              {/* Business / Account Details */}
              <EditableAccordion 
                title="BUSINESS DETAILS" 
                icon={Building2} 
                onSave={() => handleFieldSave(['business_account_id', 'business_classification', 'business_owner', 'customer_status', 'customer_service_rep', 'primary_focus', 'country', 'assigned_to', 'do_not_email'])}
                editContent={
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs text-gray-500">Business Account ID</Label><Input value={editFields.business_account_id || ''} onChange={(e) => setEditFields({...editFields, business_account_id: e.target.value})} className="mt-1" /></div>
                      <div><Label className="text-xs text-gray-500">Business Classification</Label>
                        <select value={editFields.business_classification || ''} onChange={(e) => setEditFields({...editFields, business_classification: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-md text-sm">
                          <option value="">None</option>
                          <option value="RETAIL">Retail</option>
                          <option value="PHARMACY">Pharmacy</option>
                          <option value="DME">DME</option>
                          <option value="CLINIC">Clinic</option>
                          <option value="BRC2CGM">BRC2CGM</option>
                          <option value="MEDSUP">MedSup</option>
                          <option value="TRXADE">Trxade</option>
                          <option value="LETG">LETG</option>
                          <option value="WHOLESALE">Wholesale</option>
                          <option value="SURGERY">Surgery</option>
                          <option value="EZRIRX">EzriRx</option>
                          <option value="HYBRID">Hybrid</option>
                        </select>
                      </div>
                      <div><Label className="text-xs text-gray-500">Business Owner</Label><Input value={editFields.business_owner || ''} onChange={(e) => setEditFields({...editFields, business_owner: e.target.value})} className="mt-1" /></div>
                      <div><Label className="text-xs text-gray-500">Customer Status</Label><Input value={editFields.customer_status || ''} onChange={(e) => setEditFields({...editFields, customer_status: e.target.value})} className="mt-1" /></div>
                      <div><Label className="text-xs text-gray-500">Customer Service Rep</Label><Input value={editFields.customer_service_rep || ''} onChange={(e) => setEditFields({...editFields, customer_service_rep: e.target.value})} className="mt-1" /></div>
                      <div><Label className="text-xs text-gray-500">Primary Focus</Label><Input value={editFields.primary_focus || ''} onChange={(e) => setEditFields({...editFields, primary_focus: e.target.value})} className="mt-1" /></div>
                      <div><Label className="text-xs text-gray-500">Country</Label><Input value={editFields.country || ''} onChange={(e) => setEditFields({...editFields, country: e.target.value})} className="mt-1" /></div>
                      <div><Label className="text-xs text-gray-500">Assigned To</Label><Input value={editFields.assigned_to || ''} onChange={(e) => setEditFields({...editFields, assigned_to: e.target.value})} className="mt-1" /></div>
                    </div>
                    <div className="flex items-center gap-2"><input type="checkbox" checked={editFields.do_not_email || false} onChange={(e) => setEditFields({...editFields, do_not_email: e.target.checked})} className="rounded" /><Label className="text-sm">Do Not Email</Label></div>
                  </div>
                }
              >
                <div className="space-y-2">
                  {lead.business_account_id && <div className="flex justify-between text-sm"><span className="text-gray-500">Account ID</span><span className="font-medium">{lead.business_account_id}</span></div>}
                  {lead.business_classification && <div className="flex justify-between text-sm"><span className="text-gray-500">Classification</span><span className="font-medium px-2 py-0.5 bg-blue-100 text-blue-700 rounded">{lead.business_classification}</span></div>}
                  {lead.business_owner && <div className="flex justify-between text-sm"><span className="text-gray-500">Business Owner</span><span className="font-medium">{lead.business_owner}</span></div>}
                  {lead.customer_status && <div className="flex justify-between text-sm"><span className="text-gray-500">Customer Status</span><span className="font-medium">{lead.customer_status}</span></div>}
                  {lead.customer_service_rep && <div className="flex justify-between text-sm"><span className="text-gray-500">Service Rep</span><span className="font-medium">{lead.customer_service_rep}</span></div>}
                  {lead.primary_focus && <div className="flex justify-between text-sm"><span className="text-gray-500">Primary Focus</span><span className="font-medium">{lead.primary_focus}</span></div>}
                  {lead.country && <div className="flex justify-between text-sm"><span className="text-gray-500">Country</span><span className="font-medium">{lead.country}</span></div>}
                  {lead.assigned_to && <div className="flex justify-between text-sm"><span className="text-gray-500">Assigned To</span><span className="font-medium">{lead.assigned_to}</span></div>}
                  {lead.do_not_email && <div className="flex justify-between text-sm"><span className="text-gray-500">Do Not Email</span><span className="font-medium text-red-600">YES</span></div>}
                  {!lead.business_account_id && !lead.business_classification && !lead.business_owner && <p className="text-sm text-gray-400">No business details</p>}
                </div>
              </EditableAccordion>

              {/* Sales Information */}
              <EditableAccordion 
                title="SALES INFORMATION" 
                icon={Target} 
                onSave={() => handleFieldSave(['status', 'priority'])}
                editContent={
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Status</Label>
                      <Select 
                        value={editFields.status || 'new'} 
                        onValueChange={(v) => setEditFields({...editFields, status: v})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_STATUSES.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Priority</Label>
                      <Select 
                        value={editFields.priority || 'medium'} 
                        onValueChange={(v) => setEditFields({...editFields, priority: v})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITIES.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                }
              >
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Status</p>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Priority</p>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${priorityInfo.color}`}>
                      {priorityInfo.label}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Source</p>
                    <span className="text-xs bg-[#014DB7]/10 text-[#014DB7] px-3 py-1 rounded-full font-medium">
                      {lead.source?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </EditableAccordion>

              {/* Record Information - Read Only */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="py-3 px-4 bg-gray-50">
                  <span className="text-sm font-semibold text-gray-400">RECORD INFORMATION</span>
                </div>
                <div className="p-4 bg-white">
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Lead ID</p>
                      <p className="font-mono text-xs text-gray-600">{lead.id?.slice(0, 8)}...</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Created</p>
                      <p className="text-sm text-gray-600">
                        {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Last Updated</p>
                      <p className="text-sm text-gray-600">
                        {new Date(lead.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quotes Tab */}
          {activeTab === 'quotes' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-semibold text-[#0A1628]">Quote Calculator</h3>
                  <p className="text-sm text-gray-500">Create detailed quotes with line items. Locked after emailing.</p>
                </div>
                <Button onClick={() => navigate(`/crm/leads/${id}/quote/new`)} className="bg-[#014DB7]">
                  <Plus className="w-4 h-4 mr-2" /> New Quote
                </Button>
              </div>

              {quotes.length > 0 ? (
                <div className="space-y-4">
                  {quotes.map((quote, idx) => (
                    <div key={quote.id || idx} className={`border rounded-xl overflow-hidden ${quote.is_locked ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'}`}>
                      {/* Quote Header */}
                      <div className={`px-4 py-3 flex items-center justify-between ${quote.is_locked ? 'bg-amber-100' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-3">
                          {quote.is_locked ? (
                            <Lock className="w-4 h-4 text-amber-600" />
                          ) : (
                            <ClipboardList className="w-4 h-4 text-[#014DB7]" />
                          )}
                          <div>
                            <h4 className="font-semibold text-[#0A1628]">{quote.name}</h4>
                            <p className="text-xs text-gray-500">
                              Created {new Date(quote.created_at).toLocaleDateString()}
                              {quote.sent_at && <span className="ml-2 text-amber-600">• Sent {new Date(quote.sent_at).toLocaleDateString()}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            quote.status === 'sent' ? 'bg-amber-100 text-amber-700' :
                            quote.status === 'accepted' ? 'bg-green-100 text-green-700' :
                            quote.status === 'converted' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {quote.status === 'sent' ? 'Sent' : quote.status === 'accepted' ? 'Accepted' : quote.status === 'converted' ? 'Converted' : 'Draft'}
                          </span>
                          <span className="text-lg font-bold text-[#014DB7]">
                            ${(quote.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>

                      {/* Quote Items */}
                      <div className="p-4">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 text-gray-500 font-medium">Description</th>
                              <th className="text-center py-2 text-gray-500 font-medium w-20">Qty</th>
                              <th className="text-right py-2 text-gray-500 font-medium w-28">Unit Price</th>
                              <th className="text-right py-2 text-gray-500 font-medium w-28">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(quote.items || []).map((item, itemIdx) => (
                              <tr key={itemIdx} className={`border-b border-gray-100 ${item.is_changed ? 'bg-yellow-50' : ''}`}>
                                <td className="py-2">
                                  {item.is_changed && <AlertTriangle className="w-3 h-3 text-yellow-500 inline mr-1" />}
                                  {item.description}
                                </td>
                                <td className="py-2 text-center">{item.quantity}</td>
                                <td className="py-2 text-right">${(item.unit_price || 0).toFixed(2)}</td>
                                <td className="py-2 text-right font-medium">${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td colSpan="3" className="py-3 text-right font-semibold">Total:</td>
                              <td className="py-3 text-right font-bold text-lg text-[#014DB7]">
                                ${(quote.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          </tfoot>
                        </table>

                        {quote.notes && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                            <strong>Notes:</strong> {quote.notes}
                          </div>
                        )}

                        {quote.valid_until && (
                          <p className="mt-2 text-xs text-gray-500">
                            Valid until: {new Date(quote.valid_until).toLocaleDateString()}
                          </p>
                        )}
                      </div>

                      {/* Quote Actions */}
                      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {quote.is_locked ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setQuoteToUnlock(quote.id); setShowUnlockModal(true); }}
                              className="text-xs h-8"
                            >
                              <Unlock className="w-3 h-3 mr-1" /> Unlock to Edit
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => navigate(`/crm/leads/${id}/quote/${quote.id}`)}
                              className="text-xs h-8"
                            >
                              <Edit className="w-3 h-3 mr-1" /> Edit
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDeleteQuote(quote.id)}
                            className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
                            disabled={quote.is_locked}
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          {quote.status !== 'converted' && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleConvertToInvoice(quote)}
                              className="text-xs h-8"
                            >
                              <Receipt className="w-3 h-3 mr-1" /> Convert to Invoice
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            onClick={() => handleEmailQuote(quote)}
                            className="bg-[#014DB7] text-xs h-8"
                          >
                            <Send className="w-3 h-3 mr-1" /> {quote.sent_at ? 'Resend' : 'Email Quote'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No quotes created yet</p>
                  <p className="text-sm mt-1">Create detailed quotes with line items</p>
                  <Button onClick={() => navigate(`/crm/leads/${id}/quote/new`)} className="mt-4 bg-[#014DB7]">
                    <Plus className="w-4 h-4 mr-2" /> Create First Quote
                  </Button>
                </div>
              )}

              {/* Unlock PIN Modal */}
              {showUnlockModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Lock className="w-5 h-5 text-amber-600" />
                        Unlock Quote
                      </h3>
                      <button onClick={() => { setShowUnlockModal(false); setUnlockPin(''); }} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">
                      This quote has been sent and is locked. Enter your admin PIN to unlock for editing. Changes will be logged.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label>Admin PIN</Label>
                        <Input 
                          type="password"
                          value={unlockPin}
                          onChange={(e) => setUnlockPin(e.target.value)}
                          placeholder="Enter your PIN"
                          maxLength={6}
                          className="mt-1 text-center text-2xl tracking-widest"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2 mt-6">
                      <Button variant="outline" onClick={() => { setShowUnlockModal(false); setUnlockPin(''); }}>Cancel</Button>
                      <Button onClick={handleUnlockQuote} className="bg-amber-600 hover:bg-amber-700">
                        <Unlock className="w-4 h-4 mr-2" /> Unlock
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <LeadActivityPanel api={api} leadId={id} quotes={quotes} />
          )}

          {activeTab === 'notes' && (
            <div>
              {lead.notes ? (
                <div className="bg-gray-50 rounded-xl p-5">
                  <h4 className="text-sm font-semibold text-[#0A1628] mb-3">Message from Lead</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{lead.notes}</p>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No notes or messages</p>
                  <Button className="mt-4 bg-[#014DB7] text-white rounded-xl">
                    <Plus className="w-4 h-4 mr-2" /> Add Note
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'emails' && (
            <div className="space-y-6" data-testid="lead-emails-content">
              {/* Stats Summary */}
              {emailStats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="rounded-xl border bg-blue-50 p-4 text-center">
                    <p className="text-2xl font-bold text-blue-700" data-testid="email-stat-total">{emailStats.total}</p>
                    <p className="text-xs text-blue-600 mt-1">Total Emails</p>
                  </div>
                  <div className="rounded-xl border bg-green-50 p-4 text-center">
                    <p className="text-2xl font-bold text-green-700" data-testid="email-stat-sent">{emailStats.sent}</p>
                    <p className="text-xs text-green-600 mt-1">Sent / Logged</p>
                  </div>
                  <div className="rounded-xl border bg-red-50 p-4 text-center">
                    <p className="text-2xl font-bold text-red-700" data-testid="email-stat-failed">{emailStats.failed}</p>
                    <p className="text-xs text-red-600 mt-1">Failed</p>
                  </div>
                  <div className="rounded-xl border bg-gray-50 p-4 text-center">
                    <p className="text-sm font-semibold text-gray-700" data-testid="email-stat-last">
                      {emailStats.last_email_at ? new Date(emailStats.last_email_at).toLocaleDateString() : 'Never'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Last Email</p>
                  </div>
                </div>
              )}

              {/* By Sender Breakdown */}
              {emailStats?.by_sender?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">By Sender</h4>
                  <div className="flex flex-wrap gap-2">
                    {emailStats.by_sender.map((s, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#014DB7]/10 text-[#014DB7] text-xs font-medium" data-testid={`email-sender-${i}`}>
                        <User className="w-3 h-3" />{s.name}: {s.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Email Log Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Email History</h4>
                  <Button size="sm" variant="outline" onClick={() => setShowLeadEmailModal(true)} className="text-xs rounded-xl" data-testid="compose-email-from-tab">
                    <Mail className="w-3 h-3 mr-1" /> Compose
                  </Button>
                </div>
                {loadingEmails ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="w-6 h-6 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : emailLogs.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed">
                    <Mail className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No emails sent to this lead yet</p>
                    <Button size="sm" variant="outline" className="mt-3 rounded-xl" onClick={() => setShowLeadEmailModal(true)}>Send First Email</Button>
                  </div>
                ) : (
                  <div className="rounded-xl border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Subject</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">From</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">To</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Type</th>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Date</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {emailLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50" data-testid={`email-log-${log.id}`}>
                            <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate">{log.subject || '(No subject)'}</td>
                            <td className="px-4 py-3 text-gray-600">{log.from_name || log.from_email || '-'}</td>
                            <td className="px-4 py-3 text-gray-600">{log.to_name || log.to_email || '-'}</td>
                            <td className="px-4 py-3">
                              <span className="capitalize text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{log.type || 'other'}</span>
                            </td>
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                              {log.sent_at ? new Date(log.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                log.status === 'sent' ? 'bg-green-100 text-green-700' :
                                log.status === 'logged' ? 'bg-blue-100 text-blue-700' :
                                log.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {(log.status || 'unknown').toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#0A1628]">Edit Lead</h2>
              <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input value={formData.first_name || ''} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input value={formData.last_name || ''} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input value={formData.company || ''} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Job Title</Label>
                  <Input value={formData.job_title || ''} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map(s => (<SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Assigned To</Label>
                  <Select value={formData.assigned_to || 'unassigned'} onValueChange={(v) => setFormData({ ...formData, assigned_to: v === 'unassigned' ? null : v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map(u => (<SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className="rounded-xl" />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditing(false)} className="rounded-xl">Cancel</Button>
                <Button onClick={handleSave} className="bg-[#014DB7] text-white rounded-xl">Save Changes</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <EmailComposerModal
        isOpen={showLeadEmailModal}
        onClose={() => setShowLeadEmailModal(false)}
        recipientEmail={lead?.email}
        recipientName={lead ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : ''}
        leadId={lead?.id}
        onSent={() => { if (typeof fetchEmailLogs === 'function') fetchEmailLogs(); }}
      />
    </div>
  );
};

export default LeadDetailPage;
